<template>
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div v-if="modelValue" class="import-result-dialog-overlay" @click="handleClose">
        <div class="import-result-dialog" @click.stop>
          <div class="import-result-dialog-header" :class="{ 'has-errors': hasErrors }">
            <i :class="headerIconClass"></i>
            <h4>{{ headerTitle }}</h4>
          </div>
          <div class="import-result-dialog-body">
            <!-- Statistics -->
            <div class="import-stats">
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.totalFiles') }}:</span>
                <span class="stat-value">{{ results.total_files }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.processedFiles') }}:</span>
                <span class="stat-value">{{ results.processed_files }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.totalRows') }}:</span>
                <span class="stat-value">{{ results.total_rows }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.createdDonators') }}:</span>
                <span class="stat-value">{{ results.created_donators }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.updatedDonators') }}:</span>
                <span class="stat-value">{{ results.updated_donators }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.createdStockholders') }}:</span>
                <span class="stat-value">{{ results.created_stockholders }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.updatedStockholders') }}:</span>
                <span class="stat-value">{{ results.updated_stockholders }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('import.ledghome.results.deletedStockholders') }}:</span>
                <span class="stat-value">{{ results.deleted_stockholders }}</span>
              </div>
            </div>

            <!-- Errors -->
            <div v-if="hasErrors" class="import-errors mt-4">
              <h5 class="errors-title">
                <i class="bi bi-exclamation-circle-fill"></i>
                {{ $t('import.ledghome.results.errors') }}
              </h5>
              <div v-for="(fileErrors, fileName) in results.errors" :key="fileName" class="error-file">
                <div class="file-name">{{ fileName }}</div>
                <ul class="error-list">
                  <li v-for="(error, idx) in fileErrors" :key="idx" class="error-item">
                    <span v-if="error.row" class="error-row">Row {{ error.row }}:</span>
                    <span class="error-message">{{ error.message }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="import-result-dialog-footer">
            <button class="btn-ok" @click="handleClose">
              {{ $t('common.error_dialog.ok') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  results: {
    type: Object,
    required: true,
    default: () => ({
      total_files: 0,
      processed_files: 0,
      total_rows: 0,
      created_donators: 0,
      updated_donators: 0,
      created_stockholders: 0,
      updated_stockholders: 0,
      deleted_stockholders: 0,
      errors: {}
    })
  }
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const hasErrors = computed(() => {
  return props.results.errors && Object.keys(props.results.errors).length > 0;
});

const headerIconClass = computed(() => {
  return hasErrors.value
    ? 'bi bi-exclamation-triangle-fill'
    : 'bi bi-check-circle-fill';
});

const headerTitle = computed(() => {
  return hasErrors.value
    ? $t('import.ledghome.results.titleWithErrors')
    : $t('import.ledghome.results.titleSuccess');
});

const handleClose = () => {
  emit('update:modelValue', false);
  emit('confirm');
};
</script>

<style lang="scss" scoped>
.import-result-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.import-result-dialog {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 650px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.import-result-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #E5E7EB;

  i {
    font-size: 24px;
    color: #10B981; // Green for success
  }

  &.has-errors i {
    color: #EF4444; // Red for errors
  }

  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #222222;
  }
}

.import-result-dialog-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.import-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #F9FAFB;
  border-radius: 6px;
  font-size: 14px;

  .stat-label {
    color: #6B7280;
    font-weight: 500;
  }

  .stat-value {
    color: #222222;
    font-weight: 600;
  }
}

.import-errors {
  .errors-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: #EF4444;

    i {
      font-size: 20px;
    }
  }

  .error-file {
    margin-bottom: 16px;
    padding: 16px;
    background-color: #FEF2F2;
    border-left: 4px solid #EF4444;
    border-radius: 6px;

    &:last-child {
      margin-bottom: 0;
    }

    .file-name {
      font-weight: 600;
      color: #222222;
      margin-bottom: 8px;
      font-size: 14px;
    }

    .error-list {
      margin: 0;
      padding-left: 20px;
      list-style: none;

      .error-item {
        margin-bottom: 6px;
        font-size: 13px;
        line-height: 1.5;

        &:last-child {
          margin-bottom: 0;
        }

        &::before {
          content: "•";
          color: #EF4444;
          font-weight: bold;
          display: inline-block;
          width: 1em;
          margin-left: -1em;
        }

        .error-row {
          font-weight: 600;
          color: #DC2626;
          margin-right: 4px;
        }

        .error-message {
          color: #6B7280;
        }
      }
    }
  }
}

.import-result-dialog-footer {
  padding: 16px 24px;
  border-top: 1px solid #E5E7EB;
  display: flex;
  justify-content: flex-end;

  .btn-ok {
    padding: 8px 24px;
    background: #306FC9;
    color: #FFFFFF;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: #2563EB;
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

// Transition animations
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-fade-enter-active .import-result-dialog,
.dialog-fade-leave-active .import-result-dialog {
  transition: transform 0.2s ease;
}

.dialog-fade-enter-from .import-result-dialog,
.dialog-fade-leave-to .import-result-dialog {
  transform: scale(0.95);
}
</style>
